import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Button,
  TextInput,
  Group,
  Badge,
  ActionIcon,
  Modal,
  Stack,
  Alert,
  Loader,
  Pagination,
  Select,
  Box,
  Grid,
  Divider,
  Tabs
} from '@mantine/core';
import { 
  IconSearch, 
  IconEdit, 
  IconTrash, 
  IconEye, 
  IconPlus,
  IconAdjustments,
  IconCalculator,
  IconReport,
  IconSettings
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../utils/constants';

function GLAccountsScreen() {
  const navigate = useNavigate();
  const [glAccounts, setGLAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('account_number');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [editingAccount, setEditingAccount] = useState({
    account_type: '',
    account_number: '',
    description: ''
  });

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  const accountTypes = ['Asset', 'Liability', 'Revenue', 'Expense'];

  useEffect(() => {
    fetchGLAccounts();
  }, [searchTerm, sortBy, sortOrder]);

  const fetchGLAccounts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const params = new URLSearchParams({
        sortBy,
        sortOrder
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`${API_URL}/gl-accounts?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGLAccounts(data);
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error fetching GL accounts');
      }
    } catch (error) {
      console.error('Error fetching GL accounts:', error);
      setError('Error fetching GL accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchGLAccounts();
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleAdd = () => {
    setEditingAccount({
      account_type: '',
      account_number: '',
      description: ''
    });
    setShowAddModal(true);
  };

  const handleEdit = (account) => {
    setEditingAccount({ ...account });
    setShowEditModal(true);
  };

  const handleDelete = (account) => {
    setSelectedAccount(account);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/gl-accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingAccount),
      });

      if (response.ok) {
        const newAccount = await response.json();
        setGLAccounts(prev => [...prev, newAccount.glAccount]);
        setShowAddModal(false);
        setEditingAccount({ account_type: '', account_number: '', description: '' });
        setError('');
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error creating GL account');
      }
    } catch (error) {
      console.error('Error creating GL account:', error);
      setError('Error creating GL account. Please try again.');
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/gl-accounts/${editingAccount.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingAccount),
      });

      if (response.ok) {
        const updatedAccount = await response.json();
        setGLAccounts(prev => prev.map(account => 
          account.id === updatedAccount.glAccount.id ? updatedAccount.glAccount : account
        ));
        setShowEditModal(false);
        setEditingAccount({ account_type: '', account_number: '', description: '' });
        setError('');
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error updating GL account');
      }
    } catch (error) {
      console.error('Error updating GL account:', error);
      setError('Error updating GL account. Please try again.');
    }
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/gl-accounts/${selectedAccount.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setGLAccounts(prev => prev.filter(account => account.id !== selectedAccount.id));
        setShowDeleteModal(false);
        setSelectedAccount(null);
        setError('');
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error deleting GL account');
      }
    } catch (error) {
      console.error('Error deleting GL account:', error);
      setError('Error deleting GL account. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingAccount(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getAccountTypeBadge = (type) => {
    const colors = {
      'Asset': 'green',
      'Liability': 'yellow',
      'Revenue': 'blue',
      'Expense': 'red'
    };
    return <Badge color={colors[type] || 'gray'}>{type}</Badge>;
  };

  if (loading && glAccounts.length === 0) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center">
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  return (
    <Container fluid p={0} style={{ height: '100vh' }}>
      <Grid gutter={0} style={{ height: '100%' }}>
        {/* Side Menu */}
        <Grid.Col span={1.2} style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '0.5rem', borderRight: '1px solid var(--mantine-color-gray-2)' }}>
          <Stack gap="xs">
            <Title order={6} c="dark" mb="xs" ta="center">GL Accounts</Title>
            
            {/* Primary Actions */}
            <Button 
              variant="subtle" 
              color="blue"
              fullWidth
              size="sm"
              onClick={() => setSearchTerm('')}
              leftSection={<IconSearch size="0.8rem" />}
            >
              Find
            </Button>
            <Button 
              variant="subtle" 
              color="green"
              fullWidth
              size="sm"
              onClick={handleAdd}
              leftSection={<IconPlus size="0.8rem" />}
            >
              Add
            </Button>
            <Button 
              variant="subtle" 
              color="orange"
              fullWidth
              size="sm"
              onClick={() => selectedAccount && handleEdit(selectedAccount)}
              disabled={!selectedAccount}
              leftSection={<IconEdit size="0.8rem" />}
            >
              Change
            </Button>
            <Button 
              variant="subtle" 
              color="red"
              fullWidth
              size="sm"
              onClick={() => selectedAccount && handleDelete(selectedAccount)}
              disabled={!selectedAccount}
              leftSection={<IconTrash size="0.8rem" />}
            >
              Delete
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* GL Accounts Specific Actions */}
            <Button 
              variant="subtle" 
              color="violet"
              fullWidth
              size="sm"
              leftSection={<IconAdjustments size="0.8rem" />}
            >
              Adjust
            </Button>
            <Button 
              variant="subtle" 
              color="indigo"
              fullWidth
              size="sm"
              leftSection={<IconCalculator size="0.8rem" />}
            >
              Trial Balance
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Reports and Settings */}
            <Button 
              variant="subtle" 
              color="grape"
              fullWidth
              size="sm"
              leftSection={<IconReport size="0.8rem" />}
            >
              Reports
            </Button>
            <Button 
              variant="subtle" 
              color="dark"
              fullWidth
              size="sm"
              leftSection={<IconSettings size="0.8rem" />}
            >
              Settings
            </Button>
          </Stack>
        </Grid.Col>

        {/* Main Content */}
        <Grid.Col span={10.8} style={{ padding: '1rem' }}>
          <Stack gap="lg">
            <Group justify="space-between">
              <Title order={1}>GL Accounts List</Title>
            </Group>

            {error && (
              <Alert color="red" title="Error">
                {error}
              </Alert>
            )}

            {/* Search Bar */}
            <Paper p="md" withBorder>
              <Group>
                <TextInput
                  placeholder="Search accounts by number, description, or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftSection={<IconSearch size="1rem" />}
                  style={{ flex: 1 }}
                />
                <Button onClick={handleSearch} variant="filled">
                  Search
                </Button>
                <Button 
                  onClick={() => setSearchTerm('')} 
                  disabled={!searchTerm} 
                  variant="outline"
                >
                  Clear
                </Button>
              </Group>
            </Paper>

            {/* GL Accounts Table */}
            <Paper withBorder>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th 
                      onClick={() => handleSort('account_number')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Account # {getSortIcon('account_number')}
                    </Table.Th>
                    <Table.Th 
                      onClick={() => handleSort('description')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Description {getSortIcon('description')}
                    </Table.Th>
                    <Table.Th 
                      onClick={() => handleSort('account_type')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Type {getSortIcon('account_type')}
                    </Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {glAccounts.map((account) => (
                    <Table.Tr 
                      key={account.id} 
                      onClick={() => setSelectedAccount(account)}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedAccount && selectedAccount.id === account.id ? 'var(--mantine-color-yellow-0)' : '',
                        border: selectedAccount && selectedAccount.id === account.id ? '2px solid var(--mantine-color-yellow-6)' : ''
                      }}
                    >
                      <Table.Td><Text fw={500}>{account.account_number}</Text></Table.Td>
                      <Table.Td>{account.description}</Table.Td>
                      <Table.Td>{getAccountTypeBadge(account.account_type)}</Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon 
                            variant="subtle" 
                            color="orange" 
                            onClick={(e) => { e.stopPropagation(); handleEdit(account); }}
                          >
                            <IconEdit size="1rem" />
                          </ActionIcon>
                          <ActionIcon 
                            variant="subtle" 
                            color="red" 
                            onClick={(e) => { e.stopPropagation(); handleDelete(account); }}
                          >
                            <IconTrash size="1rem" />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Add GL Account Modal */}
      <Modal opened={showAddModal} onClose={() => setShowAddModal(false)} title="Add GL Account">
        <Stack gap="md">
          <Select
            label="Account Type *"
            name="account_type"
            value={editingAccount.account_type}
            onChange={(value) => setEditingAccount(prev => ({ ...prev, account_type: value }))}
            data={accountTypes.map(type => ({ value: type, label: type }))}
            placeholder="Select Account Type"
            required
          />

          <TextInput
            label="Account Number *"
            name="account_number"
            value={editingAccount.account_number}
            onChange={(e) => setEditingAccount(prev => ({ ...prev, account_number: e.target.value }))}
            placeholder="e.g., 1000"
            required
          />

          <TextInput
            label="Description *"
            name="description"
            value={editingAccount.description}
            onChange={(e) => setEditingAccount(prev => ({ ...prev, description: e.target.value }))}
            placeholder="e.g., Cash"
            required
          />
        </Stack>

        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!editingAccount.account_type || !editingAccount.account_number || !editingAccount.description}
          >
            Add Account
          </Button>
        </Group>
      </Modal>

      {/* Edit GL Account Modal */}
      <Modal opened={showEditModal} onClose={() => setShowEditModal(false)} title="Edit GL Account">
        <Stack gap="md">
          <Select
            label="Account Type *"
            name="account_type"
            value={editingAccount.account_type}
            onChange={(value) => setEditingAccount(prev => ({ ...prev, account_type: value }))}
            data={accountTypes.map(type => ({ value: type, label: type }))}
            placeholder="Select Account Type"
            required
          />

          <TextInput
            label="Account Number *"
            name="account_number"
            value={editingAccount.account_number}
            onChange={(e) => setEditingAccount(prev => ({ ...prev, account_number: e.target.value }))}
            required
          />

          <TextInput
            label="Description *"
            name="description"
            value={editingAccount.description}
            onChange={(e) => setEditingAccount(prev => ({ ...prev, description: e.target.value }))}
            required
          />
        </Stack>

        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdate} 
            disabled={!editingAccount.account_type || !editingAccount.account_number || !editingAccount.description}
          >
            Update Account
          </Button>
        </Group>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal opened={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Delete">
        <Text>
          Are you sure you want to delete GL Account <Text component="span" fw={500}>{selectedAccount?.account_number}</Text> ({selectedAccount?.description})?
          This action cannot be undone.
        </Text>

        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={confirmDelete}>
            Delete
          </Button>
        </Group>
      </Modal>

    </Container>
  );
}

export default GLAccountsScreen;
